// src/Components/Auth/ForgotPasswordForm.tsx
import React, { useRef, useState } from 'react';
import { Row, Col, Form, FormGroup, Input, Label, Container } from 'reactstrap';
import { Link, useNavigate } from 'react-router-dom';
import { Btn, H2, H5, P, Image } from '../../AbstractElements';
import { useForgotPassword } from '../../hooks/auth/useForgotPassword';
import { useVerifyOtp } from '../../hooks/auth/useVerifyOtp';
import { useResetPassword } from '../../hooks/auth/useResetPassword';
import { dynamicImage } from '../../Utils';

export default function ForgotPasswordForm() {
    const { handleForgotPassword, loading: forgotLoading } = useForgotPassword();
    const { handleVerifyOtp, loading: verifyLoading } = useVerifyOtp();
    const { handleResetPassword, loading: resetLoading } = useResetPassword();

    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [otpParts, setOtpParts] = useState(['', '', '']);
    const otpRefs = [useRef<HTMLInputElement | null>(null), useRef<HTMLInputElement | null>(null), useRef<HTMLInputElement | null>(null)];
    const [step, setStep] = useState<'email' | 'otp' | 'password'>('email');

    const [password, setPassword] = useState('');
    const [retypePassword, setRetypePassword] = useState('');
    const [isPasswordVisible, setPasswordVisible] = useState(false);

    const onRequestOtp = async (e?: React.FormEvent) => {
        e?.preventDefault();
        if (!email) return alert('Please enter your email');
        const success = await handleForgotPassword(email);
        if (success) setStep('otp');
    };

    const onVerifyOtp = async (e?: React.FormEvent) => {
        e?.preventDefault();
        const otp = otpParts.join('');
        if (otp.length < 6) return alert('Please enter complete OTP');
        const success = await handleVerifyOtp(email, otp);
        if (success) setStep('password');
    };

    const onResetPassword = async (e?: React.FormEvent) => {
        e?.preventDefault();
        if (!password) return alert('Please enter a new password');
        if (password !== retypePassword) return alert('Passwords do not match');
        const otp = otpParts.join('');
        const success = await handleResetPassword(email, otp, password);
        if (success) {
            alert('Password reset successfully!');
            navigate('/login')
        }
    };

    const handleOtpChange = (index: number, value: string) => {
        const clean = value.replace(/\D/g, '').slice(0, 2);
        const next = [...otpParts];
        next[index] = clean;
        setOtpParts(next);
        if (clean.length === 2 && index < otpParts.length - 1) {
            otpRefs[index + 1].current?.focus();
        }
        if (clean.length === 0 && index > 0) {
            otpRefs[index - 1].current?.focus();
        }
    };

    const clearOtp = () => setOtpParts(['', '', '']);

    return (

        <div className="page-wrapper">
            <Container fluid className="p-0" style={{ backgroundColor: 'black' }}>
                <Row>
                    <Col xs={12}>
                        <div className="login-card login-dark">
                            <div>
                                {/* <div><CommonLogo /></div> */}
                                <div className="login-main">

                                    <div>
                                        <div className="text-center mb-3">
                                            <Link className="logo" to={``}>
                                                <Image
                                                    height={50}
                                                    className="for-light"
                                                    src={dynamicImage(`logo/divine_comedy.webp`)}
                                                    alt="logo"
                                                />
                                                <Image
                                                    height={50}
                                                    className="for-dark"
                                                    src={dynamicImage(`logo/divine_comedy.webp`)}
                                                    alt="logo"
                                                />
                                            </Link>
                                        </div>

                                        {step === 'email' && (
                                            <Form className="theme-form basic-form" onSubmit={onRequestOtp}>
                                                <H2>{'Reset Your Password'}</H2>

                                                <FormGroup>
                                                    <Col><Label className="pt-0">Enter your email</Label></Col>
                                                    <P className="mb-2">We will send a one-time password (OTP) to this email.</P>
                                                    <Row>
                                                        <Col>
                                                            <Input
                                                                type="email"
                                                                className="mb-1"
                                                                placeholder="Enter your email"
                                                                value={email}
                                                                onChange={(e) => setEmail(e.target.value)}
                                                                required
                                                            />
                                                        </Col>
                                                        <Col xs={12}>
                                                            <div className="text-end">
                                                                <Btn color="primary" block className="m-t-10" onClick={onRequestOtp} disabled={forgotLoading}>
                                                                    {forgotLoading ? 'Sending OTP...' : 'Send'}
                                                                </Btn>
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                </FormGroup>
                                            </Form>
                                        )}

                                        {step === 'otp' && (
                                            <Form className="theme-form basic-form" onSubmit={onVerifyOtp}>
                                                <H5 className="mt-2 f-w-700">Verify OTP</H5>

                                                <FormGroup>
                                                    <Col><Label className="pt-0">Enter OTP</Label></Col>
                                                    <Row>
                                                        {otpParts.map((part, idx) => (
                                                            <Col key={idx}>
                                                                <Input
                                                                    innerRef={otpRefs[idx]}
                                                                    className="text-center opt-text mb-1"
                                                                    type="text"
                                                                    placeholder="00"
                                                                    value={part}
                                                                    maxLength={2}
                                                                    onChange={(e) => handleOtpChange(idx, e.target.value)}
                                                                    required
                                                                />
                                                            </Col>
                                                        ))}
                                                    </Row>

                                                    <div className="mt-4 mb-4">
                                                        <span className="reset-password-link">{"If don't receive OTP? "}
                                                            <a className="btn-link text-danger" href="#" onClick={(e) => { e.preventDefault(); handleForgotPassword(email); }}> Resend</a>
                                                        </span>
                                                    </div>

                                                    <Row>
                                                        <Col xs={12} className="d-flex justify-content-between align-items-center">
                                                            <div>
                                                                <Btn color="link" onClick={() => { clearOtp(); }}>Clear</Btn>
                                                            </div>
                                                            <div>
                                                                <Btn color="primary" onClick={onVerifyOtp} disabled={verifyLoading}>
                                                                    {verifyLoading ? 'Verifying...' : 'Verify OTP'}
                                                                </Btn>
                                                                <Btn color="link" onClick={() => setStep('email')}>Change Email</Btn>
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                </FormGroup>
                                            </Form>
                                        )}

                                        {step === 'password' && (
                                            <Form className="theme-form basic-form" onSubmit={onResetPassword}>
                                                <H5 className="mt-4 f-w-700">Create Your Password</H5>

                                                <FormGroup>
                                                    <Col><Label>New Password</Label></Col>
                                                    <div className="form-input position-relative">
                                                        <Input
                                                            type={isPasswordVisible ? 'text' : 'password'}
                                                            id="password"
                                                            name="password"
                                                            required
                                                            placeholder="*********"
                                                            value={password}
                                                            onChange={(e) => setPassword(e.target.value)}
                                                        />
                                                        <div className="show-hide" onClick={() => setPasswordVisible((s) => !s)} role="button" aria-label="toggle password">
                                                            <span className={!isPasswordVisible ? 'show' : ''}></span>
                                                        </div>
                                                    </div>
                                                </FormGroup>

                                                <FormGroup>
                                                    <Col><Label>Retype Password</Label></Col>
                                                    <Input
                                                        type={isPasswordVisible ? 'text' : 'password'}
                                                        id="retypePassword"
                                                        name="retypePassword"
                                                        required
                                                        placeholder="*********"
                                                        value={retypePassword}
                                                        onChange={(e) => setRetypePassword(e.target.value)}
                                                    />
                                                    <div className="show-hide" onClick={() => setPasswordVisible((s) => !s)}></div>
                                                </FormGroup>

                                                <FormGroup className="mb-0 checkbox-checked">
                                                    <div className="text-end">
                                                        <Btn color="primary" className="w-100 mt-3" block onClick={onResetPassword} disabled={resetLoading}>
                                                            {resetLoading ? 'Resetting...' : 'Done'}
                                                        </Btn>
                                                    </div>
                                                </FormGroup>
                                            </Form>
                                        )}

                                        <P className="mt-4 mb-0 text-center">{"Already have an account?"}
                                            <Link to={`${process.env.PUBLIC_URL}/authentication/login_simple`} className="ms-2">Sign In</Link>
                                        </P>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Col>
                </Row>
            </Container>
        </div>
    );
}
